import React, { Suspense, useCallback, lazy, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tab, Tabs, useContainerWidth } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import { Switch, Route, AuthRoute, NotFound, Redirect } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { NoticeLoading } from '@fs/zion-icon'
import { getSessionId, getAnonSession } from '@fs/zion-session'
import { casService, urlUtils } from '@fs/zion-memories/memories-authorized-use-only'
import { targetEnv } from '@fs/zion-config'
import { useGroupsFlags } from './hooks'
import { GalleryProvider } from './pages/Gallery/GalleryContext'

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy

const Overview = lazy(() => import('./pages/Overview'))
const Gallery = lazy(() => import('./pages/Gallery'))
const People = lazy(() => import('./pages/People'))
const Find = lazy(() => import('./pages/Find'))
const TreeMemoriesPage = lazy(() => import('./pages/TreeMemories'))
const MemoryPage = lazy(() => import('./pages/Memory'))
const LearnMore = lazy(() => import('./pages/LearnMore'))
const SocialRedirect = lazy(() => import('./pages/SocialRedirect'))
const Restore = lazy(() => import('./pages/Restore'))
const Uploader = lazy(() => import('./pages/Uploader'))
const Admin = lazy(() => import('./pages/Admin'))

const Subnav = ({ showAdminContent }) => {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const { isOn: showDevFeaturesFlag } = useFeatureFlag('memories_dev')
  const { isOn: showReactGallery } = useFeatureFlag('memories_gallery')

  const handleReactPolymerRouting = useCallback((path) => {
    const origin = window.location.origin
    return urlUtils.isLocalHost(origin) ? `http://localhost:5000${path}` : `${origin}/photos${path}`
  }, [])

  return (
    <div style={{ display: atWidth({ default: 'none', md: 'inline' }) }}>
      <Tabs>
        <Tab title={t('memories.subnav.overview', 'Overview')} to="/" />
        <Tab
          title={t('memories.subnav.gallery', 'Gallery')}
          to={showReactGallery ? '/gallery' : handleReactPolymerRouting('/gallery')}
        />
        <Tab title={t('memories.subnav.people', 'People')} to={handleReactPolymerRouting('/people')} />
        <Tab title={t('memories.subnav.find', 'Find')} to={handleReactPolymerRouting('/find')} />
        {showDevFeaturesFlag && <Tab title="Tree" to="/tree" />}
        {showDevFeaturesFlag && <Tab title="Restore" to="/restore" />}
        {showDevFeaturesFlag && <Tab title="Uploader" to="/uploader" />}
        {showDevFeaturesFlag && showAdminContent && <Tab title="Admin" to="/admin" />}
      </Tabs>
    </div>
  )
}

function App({ setShowFooter, setFatFooter, setShowRestoreCommunityBanner }) {
  getAnonSession()
  const { personId } = useUser()
  const treeTabFallbackPid = getSessionId()?.startsWith('i0-') ? 'LRM3-8PV' : 'KNDX-MKG'
  const { isOn: showReactGallery } = useFeatureFlag('memories_gallery')
  const { isOn: showRestorationTool } = useFeatureFlag('memories_enableRestoreDashboard')
  const { isOn: showDevFeaturesFlag } = useFeatureFlag('memories_dev')
  const memoryViewerFlag = useGroupsFlags('memories_shared_memoryViewer')
  const [showSubnav, setShowSubnav] = useState(true)
  const [authorized, setAuthorized] = useState(false)
  const sessionId = getSessionId()

  try {
    casService.getPermissions({ context: 'ArtCtxOps', role: 'WebMemPrmAdmin', sessionId }).then((resp) => {
      if (resp?.data?.authorized) {
        setAuthorized(resp?.data?.authorized && ['int', 'beta', 'local'].includes(targetEnv))
      }
    })
    // eslint-disable-next-line no-empty
  } catch (error) {}

  return (
    <ErrorBoundary>
      {showSubnav && <Subnav showAdminContent={authorized} />}

      <Suspense fallback={<NoticeLoading />}>
        <Switch>
          <Route exact requirePersonalData path="/">
            <Overview setFatFooter={setFatFooter} />
          </Route>
          {showReactGallery && (
            <AuthRoute requirePersonalData path="/gallery">
              <GalleryProvider>
                <Gallery />
              </GalleryProvider>
            </AuthRoute>
          )}
          <AuthRoute requirePersonalData path="/people">
            <People />
          </AuthRoute>
          <AuthRoute requirePersonalData path="/find">
            <Find />
          </AuthRoute>
          <Route path="/learnmore">
            <LearnMore />
          </Route>
          <Route requirePersonalData path="/socialRedirect">
            <SocialRedirect />
          </Route>
          {showRestorationTool && (
            <AuthRoute requirePersonalData path="/restore">
              <Restore setShowRestoreCommunityBanner={setShowRestoreCommunityBanner} />
            </AuthRoute>
          )}
          {memoryViewerFlag && (
            <Route requirePersonalData path="/memory/:id">
              <MemoryPage setShowSubnav={setShowSubnav} setShowFooter={setShowFooter} />
            </Route>
          )}
          {showDevFeaturesFlag && (
            <>
              <AuthRoute requirePersonalData path="/tree/:pid">
                <TreeMemoriesPage />
              </AuthRoute>
              {/* Remove this route when releasing. This is only for dev and testing. */}
              <Route
                exact
                requirePersonalData
                path="/tree"
                render={() => <Redirect to={`/tree/${personId ?? treeTabFallbackPid}`} />}
              />
              <AuthRoute requirePersonalData path="/uploader">
                <Uploader />
              </AuthRoute>
              {authorized && (
                <AuthRoute requirePersonalData path="/admin">
                  <Admin />
                </AuthRoute>
              )}
            </>
          )}
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
export default App
